import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DynamicTable from "../../components/lab-dynamic-table";
import axios from "axios";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import {
  Add,
  Delete,
  Edit,
  FileDownload,
  Print,
  Search,
  Receipt,
  ClearAll,
  Payment,
  Visibility,
  Download as DownloadIcon,
} from "@mui/icons-material"; // Import Search Icon
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import InvoiceThermalReceipt from "../../components/InvoiceThermalReceipt";

import { ShareRevenueTab } from "../../components/ShareRevenue/page";
import { IncomeSummaryTab } from "../../components/Summary/page";
import { ExpenseIncomeTrackerTab } from "../../components/ExpenseIncomeTracker/page";
import { DoctorShareSummaryTab } from "../../components/DoctorShareSummary/page";
import { ProcDepWiseSummaryTab } from "../../components/ProcDepWiseSummary/page";
import { DoctorRevenueSummaryTab } from "../../components/DoctorRevenueSummary/page";
import { useSelector } from "react-redux";
import DeleteConfirmationModal from "../../components/Modals/delete-modal";
import { set } from "date-fns";
import DeletedIncome from "./DeletedInvoice/page";
import DeptProcIncomeTab from "../../components/DeptProcIncome";
import DateRangeSelector from "../../components/DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";
import TransactionTab from "./transaction";
import ClearPaymentDialog from "../../components/clearPamentDialog";
import AttachmentViewer from "../../components/AttachmentViewer";

// Add the AMOUNT_STATUS enum
export enum AMOUNT_STATUS {
  CLEAR_DUES = "Clear Dues",
  PENDING_DUES = "Pending Dues",
  ADVANCE = "Advance Payment",
  DISCOUNT = "Discount Applied",
}

// Reusable button styles
const gradientButtonStyle = {
  minWidth: { xs: "auto", md: "80px" },
  fontSize: { xs: "12px", md: "12px" },
  fontWeight: "600",
  textTransform: "none",
  borderRadius: "8px",
  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
  color: "#ffffff",
  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
    boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
    transform: "translateY(-1px)",
  },
};

// --- Account Dashboard Tab Content ---
const AccountDashboardTab = () => {
  const nowDate = new Date().toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(nowDate);
  const [toDate, setToDate] = useState(nowDate);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(100);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accountData, setAccountData] = useState<any[]>([]);
  const [summaryTotals, setSummaryTotals] = useState<any>(null);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [deleteReason, setDeleteReason] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<AMOUNT_STATUS | "">("");
  const [paymentMode, setPaymentMode] = useState("");
  const [searchParams] = useSearchParams();
  const [openClearPaymentDialog, setOpenClearPaymentDialog] = useState(false);
  const [clearPaymentData, setClearPaymentData] = useState<any>({
    invoiceId: "",
    payment_mode: "Cash",
    description: "",
    payment: 0,
    due: 0,
    advance: 0,
  });
  const [rowItem, setRowItem] = useState<any[]>([]);
  const [pendingDues, setPendingDues] = useState(0);
  const [selectedPaymentHistory, setSelectedPaymentHistory] = useState<any[]>(
    []
  );
  const [clearPaymentLoading, setClearPaymentLoading] = useState(false);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedAttachmentUrl, setSelectedAttachmentUrl] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<any | null>(null);


  const formatDateTime = (date: Date | null) => {
    if (!date) return "";
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(
      date.getDate()
    )}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // --- Search functionality with debounce ---
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchInput, 1000);

  const profile = useSelector((state: any) => state.profileSlice.profile);
  const ProcedurePermissions =
    profile?.data?.accessConfigurations?.Account || {};
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );

  const [openPrintDialogPayment, setOpenPrintDialogPayment] = useState(false);
  const navigate = useNavigate();

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
    setPage(0);
  }, [debouncedSearchTerm]);

  // Check URL query parameter for payment status
  useEffect(() => {
    const psParam = searchParams.get("ps");
    if (psParam === "advance") {
      setPaymentStatus(AMOUNT_STATUS.ADVANCE);
    } else if (psParam === "discount") {
      setPaymentStatus(AMOUNT_STATUS.DISCOUNT);
    } else if (psParam === "dues") {
      setPaymentStatus(AMOUNT_STATUS.PENDING_DUES);
    } else {
      setPaymentStatus("");
    }
  }, [searchParams]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setCount(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchUsers = async (): Promise<void> => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/findAllUsers`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setUsers(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchAcountData = async (): Promise<void> => {
    setLoading(true);
    try {
      const params: any = { pageNo: page + 1, count, fromDate, toDate };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (paymentStatus) params.paymentStatus = paymentStatus;
      if (paymentMode) params.payment_mode = paymentMode;
      if (selectedCreatedBy) params.userId = selectedCreatedBy._id;

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getIncomeRevenue`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response.data.isSuccess) {
        // Handle new response shape: { data: { invoices: [...], total: { ... } } }
        const respData = response.data.data;
        if (respData && Array.isArray(respData)) {
          setAccountData(respData || []);
          setSummaryTotals(null);
          setTotal(response.data.totalCount || 0);
        } else if (respData && respData.invoices) {
          setAccountData(respData.invoices || []);
          setSummaryTotals(respData.total || null);
          setTotal((respData.total && respData.total.totalCount) || response.data.totalCount || 0);
        } else {
          // fallback
          setAccountData(respData || []);
          setSummaryTotals(null);
          setTotal(response.data.totalCount || 0);
        }
      } else {
        toast.error(response.data.message || "Failed to fetch account data");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching account data");
      setAccountData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcountData();
  }, [page, count, fromDate, toDate, searchTerm, paymentStatus, paymentMode, selectedCreatedBy]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = () => {
    setPage(0);
    setSearchTerm(searchInput);
    fetchAcountData();
  };
  

  const handleClearPaymentSubmit = async () => {
    try {
      setClearPaymentLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authorization token is missing");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/createPayment`,
        clearPaymentData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.isSuccess) {
        toast.success(response.data.message || "Payment cleared successfully");
        handleClearPaymentClose();
        fetchAcountData();
      } else {
        toast.error(response.data.message || "Failed to clear payment");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || "Error clearing payment");
    } finally {
      setClearPaymentLoading(false);
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setDeleteReason("");
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return toast.error("Authorization token is missing");

      const config: any = {
        headers: { Authorization: `Bearer ${token}` },
      };
      if (deleteReason) config.data = { deleteReason };

      const response = await axios.delete(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/deleteIncomeById/${selectedInvoiceId}`,
        config
      );

      if (!response.data.isSuccess)
        throw new Error(response.data.message || "Failed to delete");
      toast.success(response.data.message);
      fetchAcountData();
    } catch (error: any) {
      toast.error(error.message);
    }
    handleDeleteModalClose();
  };

  const handleExcelExport = () => {
    if (!accountData.length) return toast.warn("No data to export.");

    const worksheetData = accountData.map(item => ({
      "INVOICE#": item.invoiceId,
      "MR#": item.patientInfo?.mrn,
      "PATIENT NAME": item.patientInfo?.patientName,
      SUBTOTAL: item.subTotal || item.subtotal || 0,
      DISCOUNT: item.discount || 0,
      TOTAL: item.grandTotal || 0,
      PAID: item.payment || 0,
      DUES: item.due || 0,
      "DOCTOR REVENUE": item.drRevenue || 0,
    }));
    // Calculate totals (prefer server-provided summaryTotals when available)
    const totalGrandLocal = accountData.reduce((sum, item) => sum + (Number(item?.grandTotal) || 0), 0);
    const totalPaidLocal = accountData.reduce((sum, item) => sum + (Number(item?.payment) || 0), 0);
    const totalDiscountLocal = accountData.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalDuesLocal = accountData.reduce((sum, item) => sum + (Number(item?.due) || 0), 0);
    const totalDrRevenueLocal = accountData.reduce((sum, item) => sum + (Number(item?.drRevenue) || 0), 0);

    const displayGrand = summaryTotals ? Number(summaryTotals.grandTotal || 0) : totalGrandLocal;
    const displayPaid = summaryTotals ? Number(summaryTotals.payment || 0) : totalPaidLocal;
    const displayDiscount = summaryTotals ? Number(summaryTotals.discount || 0) : totalDiscountLocal;
    const displayDues = summaryTotals ? Number(summaryTotals.due || 0) : totalDuesLocal;
    const displayDrRevenue = summaryTotals ? Number(summaryTotals.drRevenue || summaryTotals.doctorRevenue || 0) : totalDrRevenueLocal;

    // Add total row
    worksheetData.push({
      "INVOICE#": "",
      "MR#": "",
      "PATIENT NAME": "TOTAL",
      SUBTOTAL: formatCurrency(summaryTotals ? Number(summaryTotals.subTotal || 0) : totalGrandLocal),
      DISCOUNT: formatCurrency(displayDiscount),
      TOTAL: formatCurrency(displayGrand),
      PAID: formatCurrency(displayPaid),
      DUES: formatCurrency(displayDues),
      "DOCTOR REVENUE": formatCurrency(displayDrRevenue),
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Transactions");
    xlsx.writeFile(workbook, `Transactions_${fromDate}_to_${toDate}.xlsx`);
  };

 const handlePrint = () => {
    if (!accountData.length) return toast.warn("No data to print.");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("TRANSACTION REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "INVOICE#",
        "MR#",
        "PATIENT",
        "DESCRIPTION",
        "SUBTOTAL",
        "DISCOUNT",
        "TOTAL",
        "PAID",
        "DUES",
        "ADVANCE",
        "MODE",
        "DR REV",
        "CREATED BY",
        "UPDATED BY",
      ],
    ];

    const body = accountData.map(item => [
      item.invoiceId ?? "",
      item.patientInfo?.mrn ?? "",
      item.patientInfo?.patientName ?? "",
      item.description ?? "",
      formatCurrency(item.subTotal || item.subtotal || 0),
      formatCurrency(item.discount || 0),
      formatCurrency(item.grandTotal || 0),
      formatCurrency(item.payment || 0),
      formatCurrency(item.due || 0),
      formatCurrency(item.advance || 0),
      item.payment_mode ?? "",
      formatCurrency(item.drRevenue || 0),
      item.createdBy ?? "",
      item.updatedBy ?? "--",
    ]);

    // Calculate totals
    const totalGrand = accountData.reduce((sum, item) => sum + (Number(item?.grandTotal) || 0), 0);
    const totalPaid = accountData.reduce((sum, item) => sum + (Number(item?.payment) || 0), 0);
    const totalDiscount = accountData.reduce((sum, item) => sum + (Number(item?.discount) || 0), 0);
    const totalDues = accountData.reduce((sum, item) => sum + (Number(item?.due) || 0), 0);
    const totalAdvance = accountData.reduce((sum, item) => sum + (Number(item?.advance) || 0), 0);
    const totalDrRevenue = accountData.reduce((sum, item) => sum + (Number(item?.drRevenue) || 0), 0);

    // Prefer server totals if provided
    const displayPrintGrand = summaryTotals ? Number(summaryTotals.grandTotal || 0) : totalGrand;
    const displayPrintPaid = summaryTotals ? Number(summaryTotals.payment || 0) : totalPaid;
    const displayPrintDiscount = summaryTotals ? Number(summaryTotals.discount || 0) : totalDiscount;
    const displayPrintDues = summaryTotals ? Number(summaryTotals.due || 0) : totalDues;
    const displayPrintAdvance = summaryTotals ? Number(summaryTotals.advance || 0) : totalAdvance;
    const displayPrintDrRevenue = summaryTotals ? Number(summaryTotals.drRevenue || summaryTotals.doctorRevenue || 0) : totalDrRevenue;

    body.push([
      "",
      "",
      "TOTAL",
      "",
      formatCurrency(summaryTotals ? Number(summaryTotals.subTotal || 0) : 0),
      formatCurrency(displayPrintDiscount),
      formatCurrency(displayPrintGrand),
      formatCurrency(displayPrintPaid),
      formatCurrency(displayPrintDues),
      formatCurrency(displayPrintAdvance),
      "",
      formatCurrency(displayPrintDrRevenue),
      "",
      "",
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        10: { halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.save(`Transactions_${fromDate}_to_${toDate}.pdf`);
    toast.success("PDF downloaded successfully");
  };

  // Table data without actions
  const tableData = accountData.map(item => [
    item.invoiceId,
    item.patientInfo?.mrn,
    item.patientInfo?.patientName,
    item.doctorName || "--",
    item.description,
    formatCurrency(item.subTotal || item.subtotal || 0),
    formatCurrency(item.discount || 0),
    formatCurrency(item.grandTotal || 0),
    formatCurrency(item.payment || 0),
    <span
      style={{
        color: (item.due || 0) > 0 ? "red" : "inherit",
        fontWeight: (item.due || 0) > 0 ? "600" : "normal",
      }}
    >
      {formatCurrency(item.due || 0)}
    </span>,
    formatCurrency(item.advance || 0),
    item.payment_mode || "--",
    item.drRevenue ? item.drRevenue : "--",
    item.createdBy,
    formatDateTime(new Date(item.createdAt)),
    item.updatedBy || "--",
    formatDateTime(new Date(item.updatedAt)),
    // Attachment column
    item.attachmentUrl ? (
      <Box
        onClick={() => {
          setSelectedAttachmentUrl(item.attachmentUrl);
          setAttachmentDialogOpen(true);
        }}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.attachmentUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
          <img
            src={item.attachmentUrl}
            alt="Attachment"
            style={{
              width: "40px",
              height: "40px",
              objectFit: "cover",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          />
        ) : (
          <Visibility sx={{ color: "#1976d2", fontSize: "24px" }} />
        )}
      </Box>
    ) : (
      <Typography sx={{ fontSize: "12px", color: "#999" }}>--</Typography>
    ),
  ]);

  // Calculate totals
  const totalGrand = accountData.reduce(
    (sum, item) => sum + (item?.grandTotal || 0),
    0
  );
  const totalPaid = accountData.reduce(
    (sum, item) => sum + (item?.payment || 0),
    0
  );
  const totalDiscount = accountData.reduce(
    (sum, item) => sum + (item?.discount || 0),
    0
  );
  const totalDues = accountData.reduce(
    (sum, item) => sum + (item?.due || 0),
    0
  );
  const totalAdvance = accountData.reduce(
    (sum, item) => sum + (item?.advance || 0),
    0
  );
  // Prefer server-provided summary totals for the final row when available
  const displayGrand = summaryTotals ? Number(summaryTotals.grandTotal || 0) : totalGrand;
  const displayPaid = summaryTotals ? Number(summaryTotals.payment || 0) : totalPaid;
  const displayDiscount = summaryTotals ? Number(summaryTotals.discount || 0) : totalDiscount;
  const displayDues = summaryTotals ? Number(summaryTotals.due || 0) : totalDues;
  const displayAdvance = summaryTotals ? Number(summaryTotals.advance || 0) : totalAdvance;

  // Totals object for pagination footer (prefer server summary when available)
  const paginationTotals = {
    subTotal: summaryTotals ? Number(summaryTotals.subTotal || 0) : totalGrand,
    grandTotal: displayGrand,
    payment: displayPaid,
    due: displayDues,
    advance: displayAdvance,
    discount: summaryTotals ? Number(summaryTotals.discount || 0) : totalDiscount,
    // totalCount: summaryTotals ? (summaryTotals.totalCount ?? accountData.length) : accountData.length,
  };

  // Append totals row
  // Do not append totals row to the table data — keep totals in pagination and exports only
  const tableDataWithTotals = tableData;

    const handleClearPaymentOpen = (item: any) => {
     setRowItem(item);
      
    const dueAmount = item.due || 0;
    setClearPaymentData({
      invoiceId: item._id,
      payment_mode: "Cash",
      description: "",
      payment: dueAmount,
      due: 0,
      advance: 0,
    });
    setPendingDues(dueAmount);
    setSelectedPaymentHistory(item.paymentHistory || []);
    setOpenClearPaymentDialog(true);
  };
   const handleClearPaymentClose = () => {
    setOpenClearPaymentDialog(false);
    setClearPaymentData({
      invoiceId: "",
      payment_mode: "Cash",
      description: "",
      payment: 0,
      due: 0,
      advance: 0,
    });
    setPendingDues(0);
    setSelectedPaymentHistory([]);
  };

  return (
    <Box>
      <Box
        display="flex"
        sx={{ background: "#ffffff", borderRadius: "8px" }}
        justifyContent="space-between"
        alignItems="center"
        paddingY={1}
        paddingX={1}
        flexWrap="wrap"
        gap={2}
      >

        <Box
          display="flex"
          gap={2}
          alignItems="flex-end"
          flexWrap="wrap"
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
          flexGrow={1}
        >
          {ProcedurePermissions?.view_module && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handlePrint}
              sx={{
                minWidth: { xs: "auto", md: "90px" },
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                },
              }}
            >
              PDF
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExcelExport}
              sx={{
                minWidth: { xs: "auto", md: "90px" },
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                },
              }}
            >
              Excel
            </Button>
          </Box>
          )}
          <Box sx={{ width: { xs: "100%", md: "160px" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Search by Pt name or MRN
            </Typography>
            <TextField
              className="custom-input"
              size="small"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleSearch()}
              placeholder="Enter search value..."
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Payment Status
            </Typography>
            <Select
              size="small"
              value={paymentStatus}
              onChange={e =>
                setPaymentStatus(e.target.value as AMOUNT_STATUS | "")
              }
              displayEmpty
              sx={{ minWidth: { xs: "50px", md: "110px" } }}
              className="custom-input"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value={AMOUNT_STATUS.CLEAR_DUES}>
                {AMOUNT_STATUS.CLEAR_DUES}
              </MenuItem>
              <MenuItem value={AMOUNT_STATUS.PENDING_DUES}>
                {AMOUNT_STATUS.PENDING_DUES}
              </MenuItem>
              <MenuItem value={AMOUNT_STATUS.ADVANCE}>
                {AMOUNT_STATUS.ADVANCE}
              </MenuItem>
              <MenuItem value={AMOUNT_STATUS.DISCOUNT}>
                {AMOUNT_STATUS.DISCOUNT}
              </MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Payment Mode
            </Typography>
            <Select
              size="small"
              value={paymentMode}
              onChange={e => setPaymentMode(e.target.value)}
              displayEmpty
              sx={{ minWidth: { xs: "50px", md: "100px" } }}
              className="custom-input"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
            </Select>
          </Box>
          <Box sx={{ width: { xs: "100%", md: "120px" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Created By
            </Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={users}
              sx={{
                background: "#F5F5FB",
                borderRadius: "12px",
                "& fieldset": { border: "none" },
              }}
              getOptionLabel={option => option.fullName || ""}
              value={selectedCreatedBy}
              onChange={(e, newValue) => {
                setSelectedCreatedBy(newValue);
              }}
              renderInput={params => (
                <TextField {...params} placeholder="Select User" />
              )}
              clearOnEscape
            />
          </Box>
          <DateRangeSelector
            compact
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box paddingX={1} sx={{ marginTop: "16px" }}>
        <DynamicTable
          headers={[
            "INVOICE#",
            "MR#",
            "PATIENT NAME",
            "DOCTOR NAME",
            "DESCRIPTION",
            "SUBTOTAL",
            "DISCOUNT",
            "TOTAL",
            "PAID",
            "DUES",
            "ADVANCE",
            "Payment MODE",
            "DOCTOR REVENUE",
            "CREATED BY",
            "CREATED AT",
            "UPDATED BY",
            "UPDATED AT",
            "ATTACHMENT",
            "ACTIONS",
          ]}
          loading={loading}
          // totalAmount={displayGrand}
          footerTotals={paginationTotals}
          data={tableDataWithTotals}
          height={"60vh"}
          page={page}
          rowsPerPage={count}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalRows={total}
          actionButtons={[
            {
              icon: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return null;
                return (
                  <Tooltip
                    title={
                      ProcedurePermissions?.can_add_invoice
                        ? "Create invoice for this patient"
                        : "No permission to create invoice"
                    }
                    arrow
                    placement="top"
                  >
                    <Receipt
                      sx={{
                        color: ProcedurePermissions?.can_add_invoice
                          ? "#1976d2"
                          : "#ccc",
                        cursor: ProcedurePermissions?.can_add_invoice
                          ? "pointer"
                          : "not-allowed",
                        opacity: ProcedurePermissions?.can_add_invoice ? 1 : 0.5,
                      }}
                    />
                  </Tooltip>
                );
              },
              onClick: (rowIndex: number) => {
                if (
                  rowIndex >= accountData.length ||
                  !ProcedurePermissions?.can_add_invoice
                )
                  return;
                const item = accountData[rowIndex];
                navigate("/account/income", {
                  state: {
                    patient: {
                      mrn: item.patientInfo?.mrn,
                      patientName: item.patientInfo?.patientName,
                      guardiansName: item.patientInfo?.guardiansName,
                      _id: item.patientId,
                    },
                  },
                });
              },
            },
              {
              icon: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return null;
                const item = accountData[rowIndex];
                // const hasDues = item?.due > 0;
                
                return (
                  <Tooltip
                   title="Clear payment dues"

                    arrow
                    placement="top"
                  >
                    <ClearAll
                      sx={{
                        color: "#2196f3",
                        cursor: "pointer",
                        opacity: 1,
                      }}
                    />
                  </Tooltip>
                );
              },
              onClick: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return;
                const item = accountData[rowIndex];
                  handleClearPaymentOpen(item);     
              },
            },
            {
              icon: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return null;
                const item = accountData[rowIndex];
                const iconColor = item?.due === 0 ? "green" : "#888";
                return (
                  <Tooltip title="Print invoice receipt" arrow placement="top">
                    <Print sx={{ color: iconColor, cursor: "pointer" }} />
                  </Tooltip>
                );
              },
              onClick: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return;
                const item = accountData[rowIndex];
                setOpenPrintDialogPayment(false);
                setTimeout(() => {
                  setInvoiceData(item);
                  setOpenPrintDialogPayment(true);
                }, 0);
              },
            },
            {
              icon: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return null;
                const item = accountData[rowIndex];
                const isProcessed = item?.isProcessed === true;
                const canEdit = ProcedurePermissions?.can_edit && !isProcessed;
                
                return (
                  <Tooltip
                    title={
                      isProcessed
                        ? "Cannot edit - Revenue has been processed and distributed to doctors"
                        : ProcedurePermissions?.can_edit
                        ? "Edit transaction"
                        : "No permission to edit"
                    }
                    arrow
                    placement="top"
                  >
                    <Edit
                      sx={{
                        color: canEdit ? "#FFA500" : "#ccc",
                        cursor: canEdit ? "pointer" : "not-allowed",
                        opacity: canEdit ? 1 : 0.5,
                      }}
                    />
                  </Tooltip>
                );
              },
              onClick: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return;
                const item = accountData[rowIndex];
                const isProcessed = item?.isProcessed === true;
                
                if (!ProcedurePermissions?.can_edit) {
                  return;
                }
                
                if (isProcessed) {
                  toast.warning("Cannot edit - Revenue has been processed and distributed to doctors");
                  return;
                }
                
                navigate("/account/income", { state: { editData: item } });
              },
            },
            {
              icon: (rowIndex: number) => {
                if (rowIndex >= accountData.length) return null;
                return (
                  <Tooltip
                    title={
                      ProcedurePermissions?.can_delete
                        ? "Delete transaction"
                        : "No permission to delete"
                    }
                    arrow
                    placement="top"
                  >
                    <Delete
                      sx={{
                        color: ProcedurePermissions?.can_delete
                          ? "red"
                          : "#ccc",
                        cursor: ProcedurePermissions?.can_delete
                          ? "pointer"
                          : "not-allowed",
                        opacity: ProcedurePermissions?.can_delete ? 1 : 0.5,
                      }}
                    />
                  </Tooltip>
                );
              },
              onClick: (rowIndex: number) => {
                if (
                  rowIndex >= accountData.length ||
                  !ProcedurePermissions?.can_delete
                )
                  return;
                const id = accountData[rowIndex]._id;
                setSelectedInvoiceId(id);
                setIsDeleteModalOpen(true);
              },
            },
          ]}
        />
      </Box>
       <ClearPaymentDialog
        open={openClearPaymentDialog}
        onClose={handleClearPaymentClose}
        onSubmit={handleClearPaymentSubmit}
        paymentData={clearPaymentData}
        pendingDues={pendingDues}
        paymentHistory={selectedPaymentHistory}
        onPaymentDataChange={setClearPaymentData}
        loading={clearPaymentLoading}
        item={rowItem}
      />
      <InvoiceThermalReceipt
        open={openPrintDialogPayment}
        invoice={invoiceData}
        hospitalName={projectProfile?.hospitalName || "Hospital Name"}
        hospitalPhone={projectProfile?.phoneNo || "Hospital Phone"}
      />
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        message="Are you sure you want to delete this transaction?"
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
        showReasonField={true}
        reason={deleteReason}
        onReasonChange={setDeleteReason}
      />
      <AttachmentViewer
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        attachmentUrl={selectedAttachmentUrl}
        title="Invoice Attachment"
      />
    </Box>
  );
};

// --- Main AcountDashboard component ---
const AcountDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const ProcedurePermissions =
    profile?.data?.accessConfigurations?.Account || {};

  // Tab mapping for URL routing
  const tabRoutes = [
    { path: "/account/dashboard", index: 0 },
    { path: "/account/transaction", index: 1 },
    { path: "/account/invoice", index: 2 },
    { path: "/account/summary", index: 3 },
    { path: "/account/doctor-revenue", index: 4 },
    { path: "/account/doctor-share", index: 5 },
    { path: "/account/doctor-share-summary", index: 6 },
    { path: "/account/DeletedInvoice", index: 7 },
    { path: "/account/dept-proc-income", index: 8 },
    { path: "/account/proc-dep-wise-summary", index: 9 },
  ];

  // Set active tab based on current URL
  useEffect(() => {
    const currentRoute = tabRoutes.find(
      route => route.path === location.pathname
    );
    if (currentRoute) {
      setActiveTab(currentRoute.index);
    } else if (
      location.pathname === "/account" ||
      location.pathname === "/account/"
    ) {
      // Default to dashboard tab if just /account
      setActiveTab(0);
      navigate("/account/dashboard", { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedRoute = tabRoutes[newValue];
    if (selectedRoute) {
      navigate(selectedRoute.path);
    }
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "white",
          borderRadius: "4px 4px 0 0",
          px: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="account dashboard tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Dashboard"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Transaction"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Invoice"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Summary"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Doctor Revenue summary"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Doctor Share"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Doctor Share Summary"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Deleted Invoice"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Dept & Proc Income"
          />
          <Tab
            sx={{ fontSize: ".75rem", pointerEvents: "auto" }}
            label="Proc & Dep Wise Summary"
          />
        </Tabs>

        {ProcedurePermissions?.can_add_invoice && (
          <Tooltip title="Create new invoice" arrow placement="top">
            <Button
              variant="contained"
              onClick={() => navigate("/account/income")}
              size="small"
              startIcon={<Add />}
              sx={{
                minWidth: { xs: "auto", md: "140px" },
                width: { xs: "100%", md: "auto" },
                fontSize: { xs: "12px", md: "12px" },
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "8px",
                background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                color: "#ffffff",
                boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
                  boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              Create Invoice
            </Button>
          </Tooltip>
        )}
      </Box>

      {activeTab === 0 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <ExpenseIncomeTrackerTab />
        </Box>
      )}

      {activeTab === 1 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <TransactionTab/>
        </Box>
      )}
      {activeTab === 2 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <AccountDashboardTab />
        </Box>
      )}

      {activeTab === 3 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <IncomeSummaryTab />
        </Box>
      )}

      {activeTab === 4 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <DoctorRevenueSummaryTab />
        </Box>
      )}

      {activeTab === 5 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <ShareRevenueTab />
        </Box>
      )}
      {activeTab === 6 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <DoctorShareSummaryTab />
        </Box>
      )}
      {activeTab === 7 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <Typography variant="h6" align="center" color="textSecondary">
            <DeletedIncome />
          </Typography>
        </Box>
      )}
      {activeTab === 8 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <DeptProcIncomeTab />
        </Box>
      )}
      {activeTab === 9 && (
        <Box
          sx={{ pt: 2, backgroundColor: "white", borderRadius: "0 0 8px 8px" }}
        >
          <ProcDepWiseSummaryTab />
        </Box>
      )}
    </Box>
  );
};

export default AcountDashboard;
